/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Grid } from '@mui/material';
import React from 'react';
import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { getDeepProperty } from '@opengeoweb/form-fields';
import { formatIssueTime, getFieldNames } from '../utils';
import TafFormTextField from './TafFormTextField';
import type { FieldNames, TafFormRowProps } from './types';
import {
  validateChangeField,
  validateChangeFieldInterField,
} from './validations/change';
import {
  validateFirstCloud,
  validateSecondCloud,
  validateThirdCloud,
  validateFourthCloud,
} from './validations/clouds';
import { validateProbabilityField } from './validations/probability';
import {
  validateValidField,
  validateValidChangeGroupInterField,
  validatePreviousChangeGroup,
  validateOverlapWithFM,
} from './validations/validField';
import { validateVisibilityField } from './validations/visibility';
import {
  validateSecondWeatherField,
  validateThirdWeatherField,
  validateWeatherField,
} from './validations/weather';
import { validateWindField } from './validations/wind';
import { useTafConfigContext } from '../../TafWrapper/TafConfigWrapper';
import TafFormOptionsButton from '../TafFormButtons/TafFormOptionsButton';

const getDefaultValue = (fieldName: string, data: FieldValues): string => {
  if (!data) {
    return '';
  }
  return String(getDeepProperty(fieldName.split('.'), data) || '');
};

const getRequiredCrossFields = (
  fieldNames: FieldNames,
): [string[], string[], string[]] => [
  [fieldNames.valid], // main fields
  [fieldNames.change!, fieldNames.probability!], // main optional fields
  [
    fieldNames.wind,
    fieldNames.visibility,
    fieldNames.weather1,
    fieldNames.cloud1,
  ], // sub fields
];

const TafFormRow = React.forwardRef<HTMLDivElement, TafFormRowProps>(
  (
    {
      isNotRequiredForAction,
      isChangeGroup = false,
      disabled = false,
      index = -1,
      onRemoveChangeGroup,
      field = null!,
      dragHandle = null!,
      onAddChangeGroupBelow = (): void => {},
      onAddChangeGroupAbove = (): void => {},
      onClearRow = (): void => {},
      ...props
    }: TafFormRowProps,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const namePrefix = !isChangeGroup
      ? 'baseForecast'
      : `changeGroups[${index}]`;
    const fieldNames = getFieldNames(isChangeGroup, index);
    const { getValues, trigger } = useFormContext();
    const { tafConfig } = useTafConfigContext();

    // cross-field required validation
    const [mainFields, mainOptionalFields, subFields] =
      getRequiredCrossFields(fieldNames);
    const hasFieldsValue = (fields: string[]): boolean =>
      fields.some((field) => !!getValues(field));

    // Used to validate the required field for change groups: Valid
    const validateRequiredMain = (value: string): boolean | string => {
      return isNotRequiredForAction(value) !== true &&
        isChangeGroup &&
        !value.length &&
        (hasFieldsValue(subFields) ||
          hasFieldsValue(mainFields) ||
          hasFieldsValue(mainOptionalFields))
        ? 'Valid is required'
        : true;
    };

    // Used to ensure either probability or change is entered, or both
    const validateRequiredMainOptional = (value: string): boolean | string => {
      return isNotRequiredForAction(value) !== true &&
        isChangeGroup &&
        (hasFieldsValue(mainFields) || hasFieldsValue(subFields)) &&
        !hasFieldsValue(mainOptionalFields)
        ? 'Probability or change is required'
        : true;
    };

    // Used to ensure one wind, visibility, weather or cloud is entered when start editing a changegroup and all of these are empty
    const validateRequiredSub = (value: string): boolean | string => {
      return isNotRequiredForAction(value) !== true &&
        isChangeGroup &&
        (hasFieldsValue(mainFields) || hasFieldsValue(mainOptionalFields)) &&
        !hasFieldsValue(subFields)
        ? 'Wind, visibility, weather or cloud is required'
        : true;
    };

    // Used to validate the combination of change group and wind, visibility, weather and/or cloud data entered
    const validateRequiredMainFields = (
      value: string,
      fieldName: string,
    ): string | boolean => {
      const changeField = getValues(`${namePrefix}.change`);
      const visibilityField = getValues(`${namePrefix}.visibility`);
      const isFM = changeField?.trim() === 'FM';
      const isCAVOK = visibilityField?.trim() === 'CAVOK';

      // it is possible to send a empty row when the remark field has "DUE TO LACK OF DATA NO NAF" in it
      const remark = getValues('remark');
      if (
        tafConfig?.type === 'NAF' &&
        remark?.toUpperCase() === 'DUE TO LACK OF DATA NO NAF'
      ) {
        return true;
      }

      if (isNotRequiredForAction(value) !== true && !value.length) {
        if ((isChangeGroup && isFM) || !isChangeGroup) {
          // exception for CavOK
          if (isCAVOK && (fieldName === 'Clouds' || fieldName === 'Weather')) {
            return true;
          }
          // exception for visibility > 5000
          if (
            visibilityField &&
            parseInt(visibilityField.trim(), 10) >= 5000 &&
            fieldName === 'Weather'
          ) {
            return true;
          }
          return `${fieldName} is required`;
        }
      }

      return true;
    };

    const validatePreviousRow = React.useCallback(() => {
      if (isChangeGroup && index > 0) {
        const { validDateEnd, validDateStart, changeGroups } = getValues();
        const currentChangeGroup = changeGroups[index];
        const previousChangeGroup = changeGroups[index - 1];

        const isvalidPrevious =
          validateValidField(
            currentChangeGroup.valid,
            getValues('validDateStart'),
            getValues('validDateEnd'),
          ) === true;
        const isvalidNext =
          validateValidField(
            previousChangeGroup.valid,
            getValues('validDateStart'),
            getValues('validDateEnd'),
          ) === true;

        if (!isvalidPrevious || !isvalidNext) {
          return true;
        }

        return validatePreviousChangeGroup(
          currentChangeGroup,
          previousChangeGroup,
          validDateStart,
          validDateEnd,
        );
      }
      return true;
    }, [index, isChangeGroup, getValues]);

    const getFieldFromRow = (fieldname: string, rowIndex: number): string[] => {
      const indexedFieldName = `changeGroups[${rowIndex}].${fieldname}`;
      const rowValue = getValues(indexedFieldName);
      return rowValue ? [indexedFieldName] : [];
    };

    const getValidFieldFromNextRow = (): string[] =>
      getFieldFromRow('valid', index + 1);

    const validateNextRows = React.useCallback(() => {
      if (isChangeGroup) {
        const { validDateEnd, validDateStart, changeGroups } = getValues();
        const currentChangeGroup = changeGroups[index];
        const nextChangeGroups = changeGroups.slice(index + 1);
        return validateOverlapWithFM(
          currentChangeGroup,
          nextChangeGroups,
          validDateStart,
          validDateEnd,
        );
      }
      return true;
    }, [index, isChangeGroup, getValues]);

    const getPreviousValidFields = (rowIndex: number): string[] => {
      const { changeGroups } = getValues() as FieldValues;
      const previousChangeGroups = changeGroups.slice(0, rowIndex);
      const validFields = previousChangeGroups.map(
        (_element: HTMLElement, index: number) =>
          getFieldFromRow('valid', index)[0],
      );
      return validFields as string[];
    };

    const addChangeGroupBelow = React.useCallback(() => {
      onAddChangeGroupBelow(index);
    }, [onAddChangeGroupBelow, index]);

    const addChangeGroupAbove = React.useCallback(() => {
      onAddChangeGroupAbove(index);
    }, [onAddChangeGroupAbove, index]);

    const clearRow = React.useCallback(() => {
      onClearRow(index);
    }, [onClearRow, index]);

    return (
      <Grid
        container
        data-testid={`row-${namePrefix}`}
        ref={ref}
        {...props}
        className="formRow"
        sx={[
          {
            justifyContent: 'flex-start',
            marginTop: '8px',
            backgroundColor: 'geowebColors.cards.cardContainer',
            position: 'relative',
            paddingLeft: { xs: '2px', lg: '10px' },
            paddingRight: { xs: '2px', lg: '10px' },

            '&.sortable-chosen': {
              boxShadow: 1,
            },

            '&.sortable-ghost': {
              opacity: 0.5,
            },
          },
          ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        ]}
      >
        <Grid
          container
          size="grow"
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          {!isChangeGroup && (
            <>
              <TafFormTextField
                disabled
                label="Type"
                name={fieldNames.messageType!}
              />
              <TafFormTextField disabled label="Location" name="location" />
              <TafFormTextField
                disabled
                formatter={formatIssueTime}
                label="Issue time"
                name="issueDate"
                shouldPreventUppercase
              />
            </>
          )}
          {isChangeGroup ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginRight: '2px',
                  minWidth: 60,
                }}
              >
                {!disabled && dragHandle}
              </Box>
              <TafFormTextField
                autoComplete={[
                  { key: '3', value: 'PROB30' },
                  { key: '4', value: 'PROB40' },
                ]}
                defaultValue={getDefaultValue('probability', field)}
                disabled={disabled}
                label="Prob"
                name={fieldNames.probability!}
                onBlur={(): void => {
                  if (isChangeGroup) {
                    const nextValidFieldName = getValidFieldFromNextRow();
                    void trigger([
                      ...subFields,
                      ...mainOptionalFields,
                      ...mainFields,
                      ...nextValidFieldName,
                    ]);
                  }
                }}
                rules={{
                  validate: {
                    validateRequiredMainOptional,
                    validateProbabilityField,
                  },
                }}
              />
              <TafFormTextField
                autoComplete={[
                  { key: 'T', value: 'TEMPO' },
                  { key: 'B', value: 'BECMG' },
                  { key: 'F', value: 'FM' },
                ]}
                defaultValue={getDefaultValue('change', field)}
                disabled={disabled}
                label="Change"
                name={fieldNames.change!}
                onBlur={(): void => {
                  if (isChangeGroup) {
                    const nextValidFieldName = getValidFieldFromNextRow();
                    const previousValidFieldNames =
                      getPreviousValidFields(index);
                    void trigger([
                      ...subFields,
                      ...mainOptionalFields,
                      ...mainFields,
                      ...nextValidFieldName,
                      ...previousValidFieldNames,
                    ]);
                  }
                }}
                rules={{
                  validate: {
                    validateRequiredMainOptional,
                    validateChangeField,
                    validateChangeFieldInterField: (value): boolean | string =>
                      validateChangeFieldInterField(
                        value,
                        getValues(fieldNames.probability!),
                      ),
                  },
                }}
              />
            </>
          ) : null}
          <TafFormTextField
            defaultValue={getDefaultValue('valid', field)}
            // always disabled for baseforecast row
            disabled={!isChangeGroup ? true : disabled}
            label="Valid"
            name={fieldNames.valid}
            onBlur={(): void => {
              if (isChangeGroup) {
                const nextValidFieldName = getValidFieldFromNextRow();
                const previousValidFieldNames = getPreviousValidFields(index);
                void trigger([
                  ...subFields,
                  ...mainOptionalFields,
                  ...mainFields,
                  ...nextValidFieldName,
                  ...previousValidFieldNames,
                ]);
              }
            }}
            rules={{
              validate: {
                validateRequiredMain,
                validateValidField: (value: string): boolean | string =>
                  isChangeGroup
                    ? validateValidField(
                        value,
                        getValues('validDateStart'),
                        getValues('validDateEnd'),
                      )
                    : true,
                validateValidChangeGroupInterField: (
                  value,
                ): boolean | string =>
                  isChangeGroup
                    ? validateValidChangeGroupInterField(
                        value,
                        getValues(fieldNames.change!),
                        getValues(fieldNames.probability!),
                        getValues('validDateStart'),
                        getValues('validDateEnd'),
                      )
                    : true,
                validatePreviousRow,
                validateNextRows,
              },
            }}
          />

          <TafFormTextField
            autoFocus={!isChangeGroup}
            defaultValue={getDefaultValue('wind', field)}
            disabled={disabled}
            label="Wind"
            name={fieldNames.wind}
            onBlur={(): void => {
              if (isChangeGroup) {
                void trigger([...subFields, ...mainFields]);
              }
            }}
            rules={{
              validate: {
                validateRequiredMainFields: (value): boolean | string =>
                  validateRequiredMainFields(value, 'Wind'),
                validateRequiredSub,
                validateWindField: (value): boolean | string =>
                  validateWindField(
                    value,
                    getValues(fieldNames.weather1),
                    getValues(fieldNames.weather2),
                    getValues(fieldNames.weather3),
                  ),
              },
            }}
          />
          <TafFormTextField
            autoComplete={[{ key: 'C', value: 'CAVOK' }]}
            defaultValue={getDefaultValue('visibility', field)}
            disabled={disabled}
            label="Visibility"
            name={fieldNames.visibility}
            onBlur={(): void => {
              const fields = isChangeGroup
                ? [...subFields, ...mainFields]
                : subFields;
              void trigger([
                ...fields,
                fieldNames.weather1,
                fieldNames.weather2,
                fieldNames.weather3,
              ]);
            }}
            rules={{
              validate: {
                validateRequiredMainFields: (value): boolean | string =>
                  validateRequiredMainFields(value, 'Visibility'),
                validateRequiredSub,
                validateVisibilityField,
              },
            }}
          />
          <TafFormTextField
            defaultValue={getDefaultValue('weather.weather1', field)}
            disabled={disabled}
            label="Weather"
            name={fieldNames.weather1}
            onBlur={async (): Promise<boolean> => {
              const fields = isChangeGroup ? [...subFields, ...mainFields] : [];
              return trigger([
                ...fields,
                fieldNames.weather2,
                fieldNames.weather3,
                fieldNames.wind,
                fieldNames.cloud1,
              ]);
            }}
            rules={{
              validate: {
                validateRequiredMainFields: (value): boolean | string =>
                  validateRequiredMainFields(value, 'Weather'),
                validateRequiredSub,
                validateWeatherField: (value): boolean | string =>
                  validateWeatherField(
                    value,
                    getValues(fieldNames.visibility),
                    !!isChangeGroup,
                  ),
              },
            }}
          />
          <TafFormTextField
            defaultValue={getDefaultValue('weather.weather2', field)}
            disabled={disabled}
            label="Weather"
            name={fieldNames.weather2}
            onBlur={async (): Promise<boolean> => {
              const fields = isChangeGroup ? [...subFields, ...mainFields] : [];
              return trigger([
                ...fields,
                fieldNames.weather3,
                fieldNames.wind,
                fieldNames.cloud1,
              ]);
            }}
            rules={{
              validate: {
                validateSecondWeatherField: (value: string): boolean | string =>
                  validateSecondWeatherField(
                    getValues(fieldNames.weather1),
                    value,
                    getValues(fieldNames.visibility),
                  ),
              },
            }}
          />
          <TafFormTextField
            defaultValue={getDefaultValue('weather.weather3', field)}
            disabled={disabled}
            label="Weather"
            name={fieldNames.weather3}
            onBlur={async (): Promise<boolean> => {
              return trigger([fieldNames.wind, fieldNames.cloud1]);
            }}
            rules={{
              validate: {
                validateThirdWeatherField: (value: string): boolean | string =>
                  validateThirdWeatherField(
                    getValues(fieldNames.weather1),
                    getValues(fieldNames.weather2),
                    value,
                    getValues(fieldNames.visibility),
                  ),
              },
            }}
          />
          <TafFormTextField
            defaultValue={getDefaultValue('cloud.cloud1', field)}
            disabled={disabled}
            label="Cloud"
            name={fieldNames.cloud1}
            onBlur={async (): Promise<boolean> => {
              const fields = isChangeGroup ? [...subFields, ...mainFields] : [];
              return trigger([
                ...fields,
                fieldNames.cloud2,
                fieldNames.cloud3,
                fieldNames.cloud4,
              ]);
            }}
            rules={{
              validate: {
                validateRequiredMainFields: (value): boolean | string =>
                  validateRequiredMainFields(value, 'Clouds'),
                validateRequiredSub,
                validateFirstCloud: (value): boolean | string =>
                  validateFirstCloud(
                    value,
                    getValues(fieldNames.visibility),
                    getValues(fieldNames.weather1),
                    getValues(fieldNames.weather2),
                    getValues(fieldNames.weather3),
                  ),
              },
            }}
          />
          <TafFormTextField
            defaultValue={getDefaultValue('cloud.cloud2', field)}
            disabled={disabled}
            label="Cloud"
            name={fieldNames.cloud2}
            onBlur={async (): Promise<boolean> => {
              return trigger([fieldNames.cloud3, fieldNames.cloud4]);
            }}
            rules={{
              validate: {
                validateSecondCloud: (value: string): boolean | string =>
                  validateSecondCloud(
                    getValues(fieldNames.cloud1),
                    value,
                    getValues(fieldNames.visibility),
                  ),
              },
            }}
          />
          <TafFormTextField
            defaultValue={getDefaultValue('cloud.cloud3', field)}
            disabled={disabled}
            label="Cloud"
            name={fieldNames.cloud3}
            onBlur={async (): Promise<boolean> => {
              return trigger(fieldNames.cloud4);
            }}
            rules={{
              validate: {
                validateThirdCloud: (value: string): boolean | string =>
                  validateThirdCloud(
                    getValues(fieldNames.cloud1),
                    getValues(fieldNames.cloud2),
                    value,
                    getValues(fieldNames.visibility),
                  ),
              },
            }}
          />
          <TafFormTextField
            defaultValue={getDefaultValue('cloud.cloud4', field)}
            disabled={disabled}
            label="Cloud"
            name={fieldNames.cloud4}
            rules={{
              validate: {
                validateFourthCloud: (value: string): boolean | string =>
                  validateFourthCloud(
                    getValues(fieldNames.cloud1),
                    getValues(fieldNames.cloud2),
                    getValues(fieldNames.cloud3),
                    value,
                    getValues(fieldNames.visibility),
                  ),
              },
            }}
          />
        </Grid>
        {!disabled && (
          <Grid
            container
            sx={{
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              width: '3.5%',
              marginBottom: '14px',
              minWidth: 28,
            }}
          >
            <TafFormOptionsButton
              addChangeGroupAbove={addChangeGroupAbove}
              addChangeGroupBelow={addChangeGroupBelow}
              clearRow={clearRow}
              index={index}
              isChangeGroup={isChangeGroup}
              removeChangeGroup={(): void => onRemoveChangeGroup?.(index)}
            />
          </Grid>
        )}
      </Grid>
    );
  },
);

export default TafFormRow;
